import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../../components/ui/select";
import { Checkbox } from "../../../../../components/ui/uikit/checkbox";
import { CurrencyInput } from "../../../../../components/ui/uikit/currencyInput";
import { DateInput } from "../../../../../components/ui/uikit/dateInput";
import { Input } from "../../../../../components/ui/uikit/input";
import { cn } from "../../../../../lib/utils";
import { applyDigitFormatWithCaret } from "../caretPreservingDigitFormat";
import { PHONE_NUMBER_DIGITS } from "../constants";
import { formatPhoneDisplay, formatPhoneFromRaw } from "../fields/PhoneNumberInput";
import type { ComparisonRow } from "./callScriptComparison";

type CallScriptControlValue = string | number | boolean;

interface CallScriptFieldControlProperties<TKey extends string> {
  row: ComparisonRow<TKey>;
  id: string;
  value: CallScriptControlValue;
  onChange: (value: CallScriptControlValue) => void;
  disabled: boolean;
  ariaLabel: string;
  /** Submit-time validation state (employment only) — red border + aria-invalid, no popup. */
  invalid?: boolean;
  /** Re-validates this field on blur (employment's date/text/phone fields only). */
  onBlur?: () => void;
}

// ---------------------------------------------------------------------------
// CallScriptFieldControl
//
// Shared "Verified"/"Saved" cell renderer for both Call Script modals
// (employment's CallScriptModal and residence's CallScriptModalResidence).
// Switches on the same ComparisonRowKind union callScriptComparison.ts already
// models, so a row built for either screen's comparison logic renders
// correctly here. `invalid`/`onBlur` are no-ops for residence, which has no
// submit-time validation; `allowFreeText` and a select's own `placeholder`
// are no-ops for employment, which never sets them.
// ---------------------------------------------------------------------------
export function CallScriptFieldControl<TKey extends string>({
  row,
  id,
  value,
  onChange,
  disabled,
  ariaLabel,
  invalid,
  onBlur,
}: CallScriptFieldControlProperties<TKey>) {
  switch (row.kind) {
    case "checkbox": {
      return (
        <Checkbox
          id={id}
          aria-label={ariaLabel}
          checked={Boolean(value)}
          onCheckedChange={(checked) => onChange(checked === true)}
          disabled={disabled}
        />
      );
    }
    case "yesNo": {
      return (
        <Select value={value === true ? "Y" : "N"} onValueChange={(next) => onChange(next === "Y")} disabled={disabled}>
          <SelectTrigger
            id={id}
            aria-label={ariaLabel}
            className="text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) h-5 w-full"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Y">Y</SelectItem>
            <SelectItem value="N">N</SelectItem>
          </SelectContent>
        </Select>
      );
    }
    case "yesNoBlank": {
      return (
        <Select value={typeof value === "string" ? value : ""} onValueChange={onChange} disabled={disabled}>
          <SelectTrigger
            id={id}
            aria-label={ariaLabel}
            className="text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) h-5 w-full"
          >
            <SelectValue placeholder="" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Y">Y</SelectItem>
            <SelectItem value="N">N</SelectItem>
          </SelectContent>
        </Select>
      );
    }
    case "date": {
      return (
        <DateInput
          id={id}
          value={typeof value === "string" ? value : ""}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-invalid={invalid}
          className={cn(
            "text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) h-5",
            invalid && "border-destructive"
          )}
        />
      );
    }
    case "currency": {
      return (
        <CurrencyInput
          id={id}
          aria-label={ariaLabel}
          value={typeof value === "number" ? value : undefined}
          onChange={(next) => onChange(next ?? "")}
          disabled={disabled}
          className="text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) h-5"
        />
      );
    }
    case "number": {
      const stringValue = value === "" || value === undefined ? "" : String(value);
      return (
        <Input
          id={id}
          type="text"
          inputMode="decimal"
          value={stringValue}
          onChange={(event) => {
            const digits = event.target.value.replaceAll(/[^\d.]/g, "");
            onChange(digits === "" ? "" : Number(digits));
          }}
          disabled={disabled}
          aria-label={ariaLabel}
          className="text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) h-5"
        />
      );
    }
    case "select": {
      const stringValue = typeof value === "string" ? value : "";
      // CAPS2's verificationPANoCity / verificationMANoCity: with no city list
      // for the entered ZIP, the residence screen swaps the dropdown for a
      // free-text input.
      if (row.allowFreeText) {
        return (
          <Input
            id={id}
            type="text"
            value={stringValue}
            onChange={(event) => onChange(event.target.value)}
            disabled={disabled}
            aria-label={ariaLabel}
            className="text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) h-5"
          />
        );
      }
      return (
        <Select value={stringValue} onValueChange={onChange} disabled={disabled}>
          <SelectTrigger
            id={id}
            aria-label={ariaLabel}
            className="text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) h-5 w-full"
          >
            <SelectValue placeholder={row.placeholder ?? ""} />
          </SelectTrigger>
          <SelectContent>
            {row.options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    case "phone": {
      const digits = typeof value === "string" ? value.replaceAll(/\D/gu, "").slice(0, PHONE_NUMBER_DIGITS) : "";
      return (
        <Input
          id={id}
          type="tel"
          inputMode="numeric"
          value={formatPhoneDisplay(digits)}
          onChange={(event) => {
            const formatted = applyDigitFormatWithCaret(event, formatPhoneFromRaw);
            onChange(formatted.replaceAll(/\D/gu, ""));
          }}
          onBlur={onBlur}
          disabled={disabled}
          maxLength={20}
          placeholder={row.placeholder}
          aria-label={ariaLabel}
          aria-invalid={invalid}
          className={cn(
            "text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) h-5",
            invalid && "border-destructive"
          )}
        />
      );
    }
    default: {
      return (
        <Input
          id={id}
          type="text"
          value={typeof value === "string" ? value : ""}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
          disabled={disabled}
          maxLength={row.maxLength}
          placeholder={row.placeholder}
          aria-label={ariaLabel}
          aria-invalid={invalid}
          className={cn(
            "text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) h-5",
            invalid && "border-destructive"
          )}
        />
      );
    }
  }
}
